import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { StatusEnum } from "src/app/constants/status.enum";
import { ViewerDataService } from "./viewer-data.service";
import { ViewerModalService } from "./viewer-modal.service";
import { TranslateService } from "@ngx-translate/core";
import { ToastService } from "src/app/services/toast.service";
import { ActorActionEnum, Is_ANY_Download_Action } from "src/app/constants/actor-action.enum";


/**
 * THE service for the viewer that processes States and directs Actions
 */
@Injectable()
export class ViewerService {

    /**
     * Notifies when the Viewer is ready to be used
     */
    public initializationDone$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * Notifies when for any reason the Unavailable should be displayed
     */
    public requestUnavailable$: Subject<boolean> = new Subject<boolean>();
    /**
     * Stores the last emitted result of showDownloadPage, for self reference
     */
    private downloadPageIsShown = false;
    /**
     * Notifies when The downloadPage should be shown
     */
    public showDownloadPage$: Subject<boolean> = new Subject<boolean>();
    /**
     * Notifies when The Request is completed
     */
    public requestCompleted$: Subject<boolean> = new Subject<boolean>();

    translations = <any>{};
    /**
     * A boolean that verifies whether the receiving 'Current actor' is the first instance (doesn't trigger) or a later update (triggers direct popups)
     */
    actorChanged = false;
    hasUncompletedActions: boolean = false;

    public InitializeForRequest(requestGuid: string) {
        // this sets the Id and retrieves all request data (doc,actors and request ofc)
        this.viewerDataService.setRequestGuid = requestGuid;
        // the viewer sends the request WHEN Getting data fails
        this.viewerDataService.initializationFailed$.subscribe(() => {
            this.requestUnavailable$.next(true);
        });
        // this event fires when the request is deemed unavailable for the User
        this.viewerDataService.IsRequestUnavailable$.subscribe(() => {
            this.requestUnavailable$.next(true);
        });
        // This event fires when the data suggest straight to download
        this.viewerDataService.getRequestAlreadyCompleted$().subscribe(() => {
            this.downloadPageIsShown = true;
            this.showDownloadPage$.next(this.downloadPageIsShown);
        });
        // this is the event that triggers after Viewer service is ready with getting all data
        this.viewerDataService.initializationDone$.subscribe(finished => {
            if (!finished) {
                return;
            }

            // Bubble the event on, to anyone else listening (Page)
            this.initializationDone$.next(finished);

            // verify whether the request should be shown, or just straight away 'Declined'
            if (this.viewerDataService.request.status == StatusEnum.Declined) {
                // just open the rejected modal OVER the page
                this.viewerModalService.OpenRejected(this.viewerDataService.request.reasons);
            }
            else {
                // verify whether the request should be shown, or straight into the completed look
                if (this.viewerDataService.request.status == StatusEnum.Completed) {
            // trigger the Page to switch to Download
                    this.downloadPageIsShown = true;
                    this.showDownloadPage$.next(this.downloadPageIsShown);
                }
                else if (this.viewerDataService.request.status == StatusEnum.PendingSbrNexus || this.viewerDataService.request.status == StatusEnum.PendingDigipoort || this.viewerDataService.request.status == StatusEnum.Filing) {
                    // open the awaiting file OVER the page
                    this.viewerModalService.openWaitingFiling();
                }
                else {
                    if (this.viewerDataService.request.documentOwner.userId !== this.viewerDataService.get_User.sub) {
                        // if the user IS NOT THE owner, show the summary
                        // THIS USED TO ALSO TRIGGER IF THE OWNER CAME INTO THE REQUEST AS AN ACTOR
                        // BUT THAT REQUIRES MORE CHECKS :p
                        if(!Is_ANY_Download_Action(this.viewerDataService.currentActor.action)){
                            this.viewerModalService.OpenActionSummary();
                        }
                    }
                }
            }
        });
    }

    constructor(public viewerDataService: ViewerDataService, public viewerModalService: ViewerModalService, private translateService: TranslateService, private toastService: ToastService) {

        this.getTranslations();
        translateService.onLangChange.subscribe(() => {
            this.getTranslations();
        });
        // this functions Starts all the listening to Viewer Events
        this.subscribeToViewerDataEvents();

    }
    /**
     * This subscribes, Acts and propagates all events from the Data service
     */
    subscribeToViewerDataEvents() {
        // Listen to completed, this is emitted when Data sees no more Actors after USER completed
        this.viewerDataService.getCompleted$().subscribe(() => {
            if (this.viewerDataService.generateDC && !this.downloadPageIsShown) {
                // BUILD IN SMALL DELAY TO MAKE SURE Genering has a moment to show
                setTimeout(() => {
                    this.requestCompleted$.next(true);
                }, 2000);
                return;
            }
            this.requestCompleted$.next(true);
        });
        // Listen to completed, this is emitted when Data sees no more Actors after USER completed A FILE ACTION
        this.viewerDataService.getCompletedAwaitingFiling$().subscribe(() => {
            this.viewerModalService.openWaitingFiling();
        });

        // Listen to completed, this is emitted when Data sees no more user-Actors after USER completed BUT THERE ARE MORE ACTORS
        this.viewerDataService.getCompletedAwaitingOtherActors$().subscribe(() => {
            this.viewerModalService.openWaitingOtherPeople();
        });
        // Viewer keeps the current Actor synced here
        // This is updated AFTER the user completes his action
        // this means we can ACT on changing of Actors
        this.viewerDataService.getCurrentActor$().subscribe(currentActor => {
            // this is an BehaviorSubject, so it ALWAYS holds AN actor, but this also means it starts with an empty actor
            // WHEN this is triggered by a Download actor, EMIT that the PAGE should go to download
            if (currentActor && currentActor.id != undefined) {
                if(!currentActor.hasSigned && !Is_ANY_Download_Action(currentActor.action)){
                    this.hasUncompletedActions = true;
                }
                if (currentActor.action == ActorActionEnum.Download || currentActor.action == ActorActionEnum.OptionalDownload) {
                    this.downloadPageIsShown = true;
                    this.showDownloadPage$.next(this.downloadPageIsShown);
                }
            }


            // when changing actor (since this starts with NULL) BUT NOT opening the viewer with this action
            // trigger the next, this means that you can go: approve->adopt->file            
            if (this.actorChanged && currentActor && currentActor.id != undefined) {
                // since this isnt the start, trigger following actions
                if (currentActor.action == ActorActionEnum.Adopt) {
                    this.viewerModalService.openActorAdopt();
                }
                if (currentActor.action == ActorActionEnum.File) {
                    this.viewerModalService.openActorFile();
                }
            }

            // must be made true AFTER the first change of actor, and than stay true
            // this means the initial actor, will not trigger the action popups when visiting the page later
            // but it can chain them.
            this.actorChanged = this.actorChanged || (currentActor && currentActor.id != undefined);
        });
    }

    getTranslations() {
        this.translateService.get([
            'Request.View.PerformActionComplete.PreparingDocument',
            'Request.View.PerformActionComplete.Signing',
            'Request.View.PerformActionComplete.Signed',
            'Request.View.PerformActionComplete.ErrorStartingSignflow',
            'Request.View.PerformActionComplete.GivePermissionQTSPApp',
            'Request.View.PerformActionComplete.PreparingFiling',
            'Request.View.PerformActionComplete.GeneratingSignature',
            'Error.DefaultErrorTitle',
            'Error.EngineErrorDefault',
            'Error.Engine.FailedDependency',
            'Request.View.SigningDialog.ESeal',
            'Request.View.SigningDialog.Token',
            'Request.View.SigningDialog.TokenAgent',
            'Request.View.SigningDialog.Test',
            'Toasts.RequestsDownloadError',
            'Toasts.GetDocumentError',
            'Toasts.DownloadTitle',
            'Toasts.DownloadBody',
            'Toasts.Error',
            'Request.View.ReceivingEntity.Digipoort',
            'Request.View.ReceivingEntity.SBRNexus'
        ]).subscribe(translation => {
            this.translations.PreparingDocument = translation['Request.View.PerformActionComplete.PreparingDocument'];
            this.translations.Signing = translation['Request.View.PerformActionComplete.Signing'];
            this.translations.Signed = translation['Request.View.PerformActionComplete.Signed'];
            this.translations.ErrorStartingSignflow = translation['Request.View.PerformActionComplete.ErrorStartingSignflow'];
            this.translations.GivePermissionQTSPApp = translation['Request.View.PerformActionComplete.GivePermissionQTSPApp'];
            this.translations.PreparingFiling = translation['Request.View.PerformActionComplete.PreparingFiling'];
            this.translations.GeneratingSignature = translation['Request.View.PerformActionComplete.GeneratingSignature'];
            this.translations.DefaultErrorTitle = translation['Error.DefaultErrorTitle'];
            this.translations.EngineErrorDefault = translation['Error.EngineErrorDefault'];
            this.translations.FailedDependency = translation['Error.Engine.FailedDependency'];
            this.translations.ESeal = translation['Request.View.SigningDialog.ESeal'];
            this.translations.Token = translation['Request.View.SigningDialog.Token'];
            this.translations.TokenAgent = translation['Request.View.SigningDialog.TokenAgent'];
            this.translations.Test = translation['Request.View.SigningDialog.Test'];
            this.translations.toastsRequestsDownloadError = translation['Toasts.RequestsDownloadError'];
            this.translations.toastsGetDocumentError = translation['Toasts.GetDocumentError'];
            this.translations.error = translation['Toasts.Error'];
            this.translations.DownloadTitle = translation['Toasts.DownloadTitle'];
            this.translations.DownloadBody = translation['Toasts.DownloadBody'];
            this.translations.Digipoort = translation['Request.View.ReceivingEntity.Digipoort'];
            this.translations.SBRNexus = translation['Request.View.ReceivingEntity.SBRNexus'];
        });
    }


}